import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import type { PlatformUserDto } from '@his/shared';
import { get, post } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, PageHeader, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Input } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';

/**
 * Roles a staff account can hold.
 *
 * The Owner is deliberately absent: it lives in its own table, signs in with an
 * authenticator and no password, and cannot be created — or promoted into —
 * from this screen. See apps/api/src/modules/auth/owner-totp-crypto.ts.
 */
export const ROLE_NOTES: Record<string, string> = {
  platform_admin: 'Operations: provisioning, onboarding, configuration',
  support_agent: 'Support desk. No clinical data without a consented session',
  platform_finance: 'Subscriptions and invoicing. No tenant operational data',
  contact_person:
    'Client portal only: the hospitals assigned to them, their tickets and their renewals',
};

/**
 * Platform accounts (SRS 4.2.2–4.2.4, 5.3).
 *
 * Two populations, one table. Owner-console staff run the platform; contact
 * persons run an account — they sign in to the client portal and see nothing
 * beyond the hospitals named to them.
 *
 * CNIC, father's name and phone are here because a contact person is a real
 * person the company has a commercial relationship with, and those are what the
 * paperwork carries in this market.
 */
export function PlatformStaffPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('contact_person');
  const [resetting, setResetting] = useState<PlatformUserDto | null>(null);

  const staff = useQuery({
    queryKey: ['platform-staff', tab],
    queryFn: () => get<PlatformUserDto[]>(`/platform/staff${tab ? `?role=${tab}` : ''}`),
  });

  const isOwner = user?.platformRole === 'owner';
  const canManage = isOwner;

  return (
    <>
      <PageHeader
        title="Platform accounts"
        description="Who at the software company can reach what"
        actions={canManage && <Button onClick={() => navigate('/owner/staff/new')}>Add account</Button>}
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'contact_person', label: 'Contact persons' },
          { id: '', label: 'Everyone' },
        ]}
      />

      <Alert className="mb-4">
        {tab === 'contact_person'
          ? 'A contact person is assigned to hospitals from each hospital’s own page. Until they are assigned one, their portal is empty.'
          : 'Support agents cannot see clinical data. A hospital’s Super Admin must open a time-boxed, logged session first, and it expires on its own.'}
      </Alert>

      <DataTable
        rows={staff.data}
        loading={staff.isLoading} refreshing={staff.isFetching}
        error={staff.error}
        rowKey={(row) => row.id}
        empty="No accounts here yet."
        columns={[
          {
            header: 'Person',
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.name}</p>
                <p className="text-xs text-muted-foreground">
                  {row.email}
                  {row.phone ? ` · ${row.phone}` : ''}
                </p>
                {(row.fatherName || row.cnic) && (
                  <p className="text-xs text-muted-foreground">
                    {row.fatherName ? `s/o ${row.fatherName}` : ''}
                    {row.fatherName && row.cnic ? ' · ' : ''}
                    {row.cnic ?? ''}
                  </p>
                )}
              </div>
            ),
          },
          {
            header: 'Role',
            sort: (row) => row.role,
            cell: (row) => (
              <div>
                <Badge variant={row.role === 'owner' ? 'default' : 'secondary'}>{humanize(row.role, 'platform_role')}</Badge>
                <p className="mt-0.5 max-w-xs text-xs text-muted-foreground">{ROLE_NOTES[row.role]}</p>
              </div>
            ),
          },
          {
            header: 'Facilities',
            align: 'right',
            sort: (row) => (row.role === 'contact_person' ? row.assignedTenants : null),
            cell: (row) => (row.role === 'contact_person' ? row.assignedTenants : '—'),
          },
          {
            header: '2FA',
            sort: (row) => row.is2faEnabled,
            cell: (row) => (
              <Badge variant={row.is2faEnabled ? 'success' : 'warning'}>
                {row.is2faEnabled ? 'On' : 'Off'}
              </Badge>
            ),
          },
          { header: 'Last sign-in', sort: (row) => row.lastLoginAt, cell: (row) => (row.lastLoginAt ? dateTime(row.lastLoginAt) : 'Never') },
          {
            header: 'Status',
            sort: (row) => row.isActive,
            cell: (row) => (
              <Badge variant={row.isActive ? 'success' : 'destructive'}>
                {row.isActive ? 'Active' : 'Inactive'}
              </Badge>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              canManage ? (
                <div className="flex justify-end gap-2">
                  <Button variant="secondary" size="sm" onClick={() => navigate(`/owner/staff/${row.id}/edit`)}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setResetting(row)}>
                    <KeyRound className="h-4 w-4" />
                  </Button>
                </div>
              ) : null,
          },
        ]}
      />

      {resetting && <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />}
    </>
  );
}

function ResetPasswordDialog({ user, onClose }: { user: PlatformUserDto; onClose: () => void }) {
  const [password, setPassword] = useState('');

  return (
    <OwnerConfirmDialog
      open
      onClose={onClose}
      title={`Set a new password for ${user.name}`}
      confirmLabel="Set password"
      extraValid={password.length >= 12}
      warning={<>They are signed out and must choose their own password at the next sign-in.</>}
      successMessage="Password set — pass it on directly; it is shown here only."
      action={(payload) => post(`/platform/staff/${user.id}/reset-password`, { ...payload, password })}
      onDone={onClose}
    >
      <Field label="New password" required hint="At least 12 characters">
        <Input value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
    </OwnerConfirmDialog>
  );
}
